/**
 * PrintFileTargetRow — ONE required print file (a side × format) and the
 * operator's progress against it.
 *
 * Extracted (BLU-663/BLU-855 review) because the ResolveDialog's per-side
 * repair form and the bulk upload modal were each rendering the same row by
 * hand, with different labels and colours for the same server state. The
 * label/colour/icon derivation itself lives in `printFileTargetState.ts`.
 *
 * Hierarchy (PRODUCTION_DESIGN §4.1/§4.2): a file that still needs the
 * operator DOMINATES — full dropzone, `text-base` copy, amber. A file that is
 * already there collapses to one quiet line (tick + filename + "View existing
 * file") with a "Replace this file" escape, so a dual product's one missing
 * side isn't buried under two satisfied ones.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, UploadCloud } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { PrintFileDropzone } from './PrintFileDropzone';
import {
  describePrintFileTarget,
  type PrintFileTargetPhase,
  type PrintFileTargetSummary,
} from '../printFileTargetState';

interface PrintFileTargetRowProps {
  target: PrintFileTargetSummary;
  /** Operator-readable side name ("Front" / "Back") — already translated. */
  sideLabel: string;
  file: File | null;
  phase: PrintFileTargetPhase;
  /** Verified/failed detail from the server, already translated. */
  message?: string | null;
  /** Locked by something outside this row (metadata block, unknown fleet, a
   *  sibling upload in flight, the bulk run). */
  disabled?: boolean;
  /** The PUT already landed — the action re-verifies instead of re-uploading. */
  uploaded?: boolean;
  onFileChange: (file: File | null) => void;
  /** Per-row submit (the dialog). Omitted by the bulk modal, whose single
   *  footer button runs every row. */
  onSubmit?: () => void;
  compact?: boolean;
  /** Unique across the whole surface — the dropzone's label/input id. */
  idSuffix: string;
  testId?: string;
}

export function PrintFileTargetRow({
  target,
  sideLabel,
  file,
  phase,
  message,
  disabled = false,
  uploaded = false,
  onFileChange,
  onSubmit,
  compact = false,
  idSuffix,
  testId,
}: PrintFileTargetRowProps) {
  const { t } = useTranslation('production');
  // A satisfied file stays collapsed until the operator asks to replace it.
  const [replacing, setReplacing] = useState(false);
  const presentation = describePrintFileTarget(target);
  const busy = phase === 'uploading' || phase === 'verifying';
  const settled = target.status === 'present' || phase === 'success';
  const collapsed = settled && !file && !replacing && !busy;
  const locked = disabled || target.locked || busy;
  const formatLabel = target.format.toUpperCase();

  const statusLine = (
    <p className={cn('flex items-center gap-2 text-base', presentation.className)}>
      <presentation.Icon
        className={cn('h-5 w-5 shrink-0', presentation.spin && 'motion-safe:animate-spin')}
        aria-hidden="true"
      />
      <span>
        {formatLabel} · {t(presentation.labelKey)}
      </span>
    </p>
  );

  const existingFileLink = target.downloadUrl ? (
    <a
      className="inline-flex min-h-12 items-center text-base underline"
      href={target.downloadUrl}
      target="_blank"
      rel="noreferrer"
    >
      {t('issues.printFiles.upload.viewExisting')}
    </a>
  ) : null;

  if (collapsed) {
    return (
      <div
        className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-t pt-3"
        data-testid={testId}
      >
        <div className="min-w-0">
          <p className="break-all font-mono text-base">{target.filename}</p>
          {statusLine}
        </div>
        <div className="flex items-center gap-2">
          {existingFileLink}
          <Button
            type="button"
            variant="ghost"
            className="h-12 text-base text-muted-foreground"
            disabled={disabled || target.locked}
            onClick={() => setReplacing(true)}
          >
            {t('issues.printFiles.upload.replaceFile')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2 border-t pt-3" data-testid={testId}>
      <p className="break-all font-mono text-base">{target.filename}</p>
      {statusLine}
      {existingFileLink}
      <PrintFileDropzone
        idSuffix={idSuffix}
        file={file}
        accept={`.${target.format}`}
        disabled={locked}
        compact={compact}
        onFileChange={onFileChange}
      />
      {busy && (
        <p className="flex items-center gap-2 text-base text-muted-foreground">
          <Loader2 className="h-5 w-5 motion-safe:animate-spin" aria-hidden="true" />
          {t(
            phase === 'uploading'
              ? 'issues.printFiles.upload.uploading'
              : 'issues.printFiles.upload.verifying',
          )}
        </p>
      )}
      {phase === 'error' && message && (
        <Alert variant="destructive" role="alert">
          <AlertDescription className="text-base">{message}</AlertDescription>
        </Alert>
      )}
      {phase !== 'error' && message && (
        <p className="text-base text-emerald-700 dark:text-emerald-300">{message}</p>
      )}
      {onSubmit && (
        <Button
          className="h-12 gap-2 text-base"
          disabled={!file || locked}
          onClick={onSubmit}
        >
          {busy ? (
            <Loader2 className="h-5 w-5 motion-safe:animate-spin" aria-hidden="true" />
          ) : (
            <UploadCloud className="h-5 w-5" aria-hidden="true" />
          )}
          {uploaded
            ? t('issues.printFiles.upload.verifyAgain')
            : t('issues.printFiles.upload.uploadTarget', {
                side: sideLabel,
                format: formatLabel,
              })}
        </Button>
      )}
    </div>
  );
}
